import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import {
  useGetRolesQuery,
  useGetRoleTagsQuery,
  useGetRoleUsersQuery,
  useAssignUserToRoleMutation,
  useUnassignUserFromRoleMutation,
  RoleListItem,
  RoleUserItem,
} from "../../../app/services/roles";
import { useSearchUsersQuery } from "../../../app/services/users";
import type { FollowUser } from "../../../app/services/follows";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import AvatarStack, {
  AvatarStackItem,
} from "../../../components/avatar-stack/AvatarStack";
import Input from "../../../components/form-item/Input";
import FilterChipBar from "../../../components/filter-chip/FilterChipBar";
import type { ShotTag } from "../../../app/services/projects";
import { roleTagToShotTag } from "../../../utils/roleTagAdapter";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

import RoleThumbnail from "./RoleThumbnail";
import { useStatusIndicator } from "../hooks/useStatusIndicator";

const Layout = styled.div.attrs({ className: "u-w100" })`
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(320px, 1.5fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Pane = styled.div.attrs({ className: "u-col u-w100 u-gap12" })``;

const PaneHeader = styled.div.attrs({
  className: "u-row u-gap8 u-align-center",
})``;

const PaneTitle = styled.h3.attrs({ className: "heading-13" })`
  color: var(--soremo-text);
`;

const RolesList = styled.ul.attrs({ className: "u-col u-w100" })`
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 480px;
  overflow-y: auto;
`;

const RoleRow = styled.li<{ $active: boolean }>`
  width: 100%;
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--soremo-blue)" : "var(--soremo-border)"};
  background: ${({ $active }) =>
    $active ? "var(--soremo-bg-blue, rgba(0,154,206,0.06))" : "#fff"};
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: var(--soremo-blue);
    }
  }
`;

const RoleRowText = styled.div.attrs({ className: "u-col u-fill u-gap2" })``;

const RoleId = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
  font-family: "din-2014", sans-serif;
  font-weight: 800;
`;

const Hint = styled.p.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
`;

const SearchBar = styled.div.attrs({
  className: "u-row u-w100 u-gap8 u-align-center",
})``;

/**
 * IntersectionObserver の sentinel。可視領域に入ったら次ページを取得する。
 * 高さ 1px だと検出が不安定なケースがあるため余裕を持たせ、`rootMargin: 200px`
 * 側で先読みも併用する。
 */
const Sentinel = styled.div`
  width: 100%;
  height: 24px;
`;

/** 検索結果一覧のページサイズ。`/api/users/` を offset/limit で順次フェッチする。 */
const SEARCH_PAGE_LIMIT = 30;

/** 検索クエリ入力 → API 発火までのデバウンス時間 (ms)。 */
const SEARCH_DEBOUNCE_MS = 250;

const toAvatarStackItem = (user: {
  id: number;
  avatar_url?: string | null;
  display_name_jp: string;
}): AvatarStackItem => ({
  id: user.id,
  userId: user.id,
  avatar: user.avatar_url ?? undefined,
  displayName: user.display_name_jp,
});

const CreatorRolesManager: React.FC = () => {
  const { data: tagsData } = useGetRoleTagsQuery();
  const [selectedTagIdStr, setSelectedTagIdStr] = useState<string | null>(null);
  const selectedTagId = useMemo(
    () =>
      selectedTagIdStr === null
        ? undefined
        : Number.parseInt(selectedTagIdStr, 10),
    [selectedTagIdStr],
  );

  const { data: rolesData, isFetching: isFetchingRoles } = useGetRolesQuery({
    tag_id: selectedTagId,
    limit: 200,
  });

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  const tagsForBar: ShotTag[] = useMemo(
    () => (tagsData?.data?.tags ?? []).map(roleTagToShotTag),
    [tagsData],
  );

  const roles: RoleListItem[] = useMemo(
    () => rolesData?.data?.roles ?? [],
    [rolesData],
  );

  // ロール一覧読込直後に未選択であれば先頭ロールを自動選択
  useEffect(() => {
    if (selectedRoleId === null && roles.length > 0) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  const selectedRole: RoleListItem | undefined = useMemo(
    () => roles.find((r) => r.id === selectedRoleId),
    [roles, selectedRoleId],
  );

  const {
    data: roleUsersData,
    isFetching: isFetchingUsers,
  } = useGetRoleUsersQuery(
    { role_id: selectedRoleId ?? 0, limit: 100 },
    { skip: selectedRoleId === null },
  );

  const assignedUsers: RoleUserItem[] = useMemo(
    () => roleUsersData?.data?.users ?? [],
    [roleUsersData],
  );
  const assignedUserIds = useMemo(
    () => new Set(assignedUsers.map((u) => u.id)),
    [assignedUsers],
  );

  // ===== 検索結果（無限スクロール） =====
  // クエリが空でも CREATOR ユーザー全件を `limit/offset` で順次取得する。
  // クエリ変更時は accumulator をリセットし最初のページから再取得する。
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const trimmedQuery = debouncedQuery.trim();

  const [searchOffset, setSearchOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<FollowUser[]>([]);

  // クエリが変わったら offset と累積を初期化
  useEffect(() => {
    setSearchOffset(0);
    setAccumulated([]);
  }, [trimmedQuery]);

  const {
    data: searchResults,
    isFetching: isSearching,
  } = useSearchUsersQuery({
    q: trimmedQuery,
    is_creator: true,
    limit: SEARCH_PAGE_LIMIT,
    offset: searchOffset,
  });

  // ページ取得結果を累積（offset=0 のときは置き換え、それ以外は追記して重複除外）
  useEffect(() => {
    const data = searchResults?.data;
    if (!data) return;
    setAccumulated((prev) => {
      if (data.offset === 0) return data.users;
      const seen = new Set(prev.map((u) => u.id));
      return [...prev, ...data.users.filter((u) => !seen.has(u.id))];
    });
  }, [searchResults]);

  const hasMore = !!searchResults?.data?.has_next;
  const totalCount = searchResults?.data?.count ?? 0;

  const loadMore = useCallback(() => {
    if (!hasMore || isSearching) return;
    setSearchOffset((prev) => prev + SEARCH_PAGE_LIMIT);
  }, [hasMore, isSearching]);

  // sentinel が可視領域に入ったら次ページ
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const candidates: FollowUser[] = accumulated;

  const [assignUserToRole] = useAssignUserToRoleMutation();
  const [unassignUserFromRole] = useUnassignUserFromRoleMutation();
  const { status, indicate } = useStatusIndicator();

  /** 連打抑止: 操作中のユーザー ID を記録 */
  const [busyUserIds, setBusyUserIds] = useState<Set<number>>(new Set());

  const setBusy = useCallback((userId: number, busy: boolean) => {
    setBusyUserIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(userId);
      else next.delete(userId);
      return next;
    });
  }, []);

  const assign = useCallback(
    async (userId: number) => {
      if (selectedRoleId === null) return;
      try {
        setBusy(userId, true);
        indicate("saving");
        await assignUserToRole({ userId, roleId: selectedRoleId }).unwrap();
        indicate("saved");
      } catch (e) {
        console.error("Failed to assign role:", e);
        indicate("error", 3000);
      } finally {
        setBusy(userId, false);
      }
    },
    [assignUserToRole, selectedRoleId, indicate, setBusy],
  );

  const unassign = useCallback(
    async (userId: number) => {
      if (selectedRoleId === null) return;
      try {
        setBusy(userId, true);
        indicate("deleting");
        await unassignUserFromRole({
          userId,
          roleId: selectedRoleId,
        }).unwrap();
        indicate("saved");
      } catch (e) {
        console.error("Failed to unassign role:", e);
        indicate("error", 3000);
      } finally {
        setBusy(userId, false);
      }
    },
    [unassignUserFromRole, selectedRoleId, indicate, setBusy],
  );

  /** 検索結果から候補をクリックしたときの toggle。assigned なら即時 unassign、未割当なら assign。 */
  const handleCandidateClick = useCallback(
    (user: FollowUser) => {
      const already = assignedUserIds.has(user.id);
      if (already) {
        void unassign(user.id);
      } else {
        void assign(user.id);
      }
    },
    [assignedUserIds, assign, unassign],
  );

  return (
    <>
      <Hint>
        左でロールを選び、右ペインで「現在そのロールを担っているユーザー」を確認できます。下部のユーザー一覧（CREATOR 全員）からアバターをクリックして紐付けの ON / OFF を切り替えます。スクロールすると追加で読み込まれます。
      </Hint>

      <div className="u-col u-w100 u-gap12">
        <FilterChipBar
          items={tagsForBar}
          selectedId={selectedTagIdStr}
          onSelect={setSelectedTagIdStr}
        />
      </div>

      <Layout>
        <Pane>
          <PaneHeader>
            <PaneTitle>ロール</PaneTitle>
            <span className="bodytext-11 u-text-light-gray">
              ({roles.length})
            </span>
          </PaneHeader>
          {isFetchingRoles && roles.length === 0 ? (
            <Hint>読み込み中...</Hint>
          ) : roles.length === 0 ? (
            <Hint>該当するロールがありません。</Hint>
          ) : (
            <RolesList>
              {roles.map((role) => (
                <RoleRow
                  key={role.id}
                  $active={selectedRoleId === role.id}
                  onClick={() => setSelectedRoleId(role.id)}
                >
                  <RoleThumbnail roleId={role.id} size={40} />
                  <RoleRowText>
                    <div className="u-row u-gap4 u-align-center">
                      <RoleId>#{role.id}</RoleId>
                      <span className="heading-13">{role.role_name_jp}</span>
                    </div>
                    {role.description && (
                      <span className="bodytext-11 u-text-light-gray">
                        {role.description}
                      </span>
                    )}
                  </RoleRowText>
                </RoleRow>
              ))}
            </RolesList>
          )}
        </Pane>

        <Pane>
          {selectedRole ? (
            <>
              <PaneHeader>
                <RoleThumbnail roleId={selectedRole.id} size={32} />
                <PaneTitle>
                  「{selectedRole.role_name_jp}」を担うユーザー
                </PaneTitle>
                <span className="bodytext-11 u-text-light-gray">
                  ({assignedUsers.length})
                </span>
              </PaneHeader>

              {isFetchingUsers && assignedUsers.length === 0 ? (
                <Hint>読み込み中...</Hint>
              ) : assignedUsers.length === 0 ? (
                <Hint>まだ誰も紐付いていません。下から追加してください。</Hint>
              ) : (
                <AvatarStack
                  items={assignedUsers.map(toAvatarStackItem)}
                  size={40}
                  selectedIds={assignedUsers.map((u) => u.id)}
                  disabledIds={Array.from(busyUserIds)}
                  onSelect={(id) => void unassign(Number(id))}
                  ariaLabel="このロールを担っているユーザー"
                />
              )}

              <PaneHeader>
                <PaneTitle>ユーザー一覧</PaneTitle>
                <span className="bodytext-11 u-text-light-gray">
                  ({candidates.length}
                  {totalCount > candidates.length ? `/${totalCount}` : ""})
                </span>
              </PaneHeader>
              <SearchBar>
                <Input
                  type="search"
                  placeholder="氏名・ステージネームで絞り込み (CREATOR 全員)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </SearchBar>

              {isSearching && candidates.length === 0 ? (
                <Hint>読み込み中...</Hint>
              ) : candidates.length === 0 ? (
                <Hint>該当するユーザーがいません。</Hint>
              ) : (
                <>
                  <AvatarStack
                    items={candidates.map(toAvatarStackItem)}
                    size={40}
                    selectedIds={candidates
                      .filter((u) => assignedUserIds.has(u.id))
                      .map((u) => u.id)}
                    disabledIds={Array.from(busyUserIds)}
                    onSelect={(id) => {
                      const user = candidates.find((u) => u.id === Number(id));
                      if (user) handleCandidateClick(user);
                    }}
                    endSlot={hasMore ? <Sentinel ref={sentinelRef} /> : null}
                    ariaLabel="ユーザー候補一覧"
                  />
                  {isSearching && hasMore && <Hint>読み込み中...</Hint>}
                </>
              )}
            </>
          ) : (
            <Hint>左でロールを選択してください。</Hint>
          )}
        </Pane>
      </Layout>

      <StatusIndicator status={status} withBottomAppBar={false} />
    </>
  );
};

export default CreatorRolesManager;
